// website/utils/resultsLabFixtures.js
//
// Fixture loader for the Results Lab (admin-only display/sort/filter vacuum).
//
// Real-data fixtures already exist on disk as E2E test output:
//   scripts/output/human-search-e2e-*.json     (full `items[]` per query)
//   scripts/output/production-search-e2e-*.json  (summary only — no items)
//
// Each human-search E2E file has shape:
//   { timestamp, base, ticker, total, passed, failed,
//     results: [{ q, items: [rawBackendItem, ...], count, referenceTime, ... }] }
//
// Each raw backend item already carries the full shape the live pipeline
// produces: bookOdds, ev_percent, ev_percent_clean, devig_methods, market,
// sportsbook, deep_link, _ev_source, booksCalculated, etc. So we run them
// through the SAME production transforms the live screen uses:
//
//   transformStraightItems(items)      → card-shaped results
//   hydrateResultsForRender(results)   → UnifiedBetCard-ready fetchedOptions
//
// Bugs found in the lab are bugs in production; fixes land in the real path.
//
// All file IO is web-only (fetch + FileReader). On native we no-op. The lab
// itself is admin-only and web-only, so this is fine.

import { transformStraightItems, hydrateResultsForRender } from '../services/searchPipeline';
import { extractResultFacets } from './sgpFlowUtils';

function parseDateMs(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return v > 1e12 ? v : v * 1000;
  if (typeof v === 'object') {
    if (typeof v._seconds === 'number') return v._seconds * 1000 + Math.floor((v._nanoseconds || 0) / 1e6);
    if (typeof v.seconds === 'number') return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
    if (v instanceof Date) return v.getTime();
    return null;
  }
  const ms = new Date(v).getTime();
    return Number.isFinite(ms) ? ms : null;
}

/** Always a string. Live nlOdds sometimes stamps `q`/`query` as {league, sportsbook}. */
export function formatQueryLabel(v, fallback = 'Query') {
  const fb = (typeof fallback === 'string') ? fallback : 'Query';
  const isSentinel = (s) => /^(all|any|none|null|undefined|query|-)$/i.test(String(s).trim());
  if (v == null || v === '') return fb;
  if (typeof v === 'string' || typeof v === 'number') {
    const s = String(v).trim();
    return (!s || isSentinel(s) || /^all\s*[·\-–]\s*all$/i.test(s)) ? fb : s;
  }
  if (typeof v === 'object') {
    const nested = v.q || v.prompt || v.query || v.text;
    if (typeof nested === 'string' && nested.trim() && !isSentinel(nested)) return nested.trim();
    const parts = [v.league, v.sportsbook, v.sport, v.market]
      .filter((x) => typeof x === 'string' && x.trim() && !isSentinel(x));
    if (parts.length) return parts.join(' · ');
  }
  return fb;
}

// Normalize nlOdds / capture payloads to the E2E fixture shape the lab expects.
// Mirrors cacheService.js response normalization (flatten nested item.odds, etc.).
export function normalizeNlOddsPayload(data, prompt = '') {
  const out = data && typeof data === 'object' ? { ...data } : {};
  if (out.odds && Array.isArray(out.odds) && (!out.results || !out.results[0]?.items?.length)) {
    out.results = [{ q: prompt, query: prompt, items: out.odds }];
  }
  if (!Array.isArray(out.results) || out.results.length === 0) {
    const items = out.items || out.transformedResults || [];
    out.results = [{ q: prompt, query: prompt, items: Array.isArray(items) ? items : [] }];
  }
  if (out.results?.[0]?.items) {
    const flatItems = [];
    for (const item of out.results[0].items) {
      if (item?.odds && Array.isArray(item.odds)) flatItems.push(...item.odds);
      else flatItems.push(item);
    }
    out.results[0].items = flatItems;
    if (!out.results[0].q || typeof out.results[0].q !== 'string') {
      out.results[0].q = formatQueryLabel(out.results[0].q, prompt);
      out.results[0].query = formatQueryLabel(out.results[0].query, out.results[0].q);
    }
  }
  return out;
}

// Freeze reference time just after the latest game in the fixture so the hook's
// stale-game filter does not drop historical capture/E2E payloads in the lab.
export function inferReferenceTimeFromItems(items) {
  let maxMs = 0;
  for (const item of items || []) {
    const ms = parseDateMs(
      item?.start_date || item?.commence_time || item?.event_date || item?.game_time || item?.date
    );
    if (ms != null && ms > maxMs) maxMs = ms;
  }
  return maxMs > 0 ? new Date(maxMs + 60 * 60 * 1000).toISOString() : null;
}

// Convert any date shape the backend emits into an ISO string.
function toISO(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') {
    const ms = v > 1e12 ? v : v * 1000;
    return new Date(ms).toISOString();
  }
  if (typeof v === 'object') {
    if (typeof v._seconds === 'number') {
      return new Date(v._seconds * 1000 + Math.floor((v._nanoseconds || 0) / 1e6)).toISOString();
    }
    if (typeof v.seconds === 'number') {
      return new Date(v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6)).toISOString();
    }
    if (v instanceof Date) return v.toISOString();
    return null;
  }
  const ms = new Date(v).getTime();
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}

// Walk an item and normalize every date-shaped field we know the pipeline
// reads. We do NOT mutate the original (fixtures should be reloadable).
function normalizeItemDates(item) {
  const out = { ...item };
  const dateFields = ['start_date', 'commence_time', 'event_date', 'game_time', 'start_time', '_start_date'];
  for (const f of dateFields) {
    if (out[f] != null) {
      const iso = toISO(out[f]);
      if (iso) out[f] = iso;
    }
  }
  if (out.raw && typeof out.raw === 'object') {
    out.raw = normalizeItemDates(out.raw);
  }
  return out;
}

// Load one query's items from an E2E fixture file and produce the exact
// `fetchedOptions` shape the live screen consumes.
//
// Returns:
//   {
//     query, referenceTime, classification,
//     rawItems,            // pre-transform (for diagnostics)
//     fetchedOptions,      // post-hydrate (what the hook sees)
//     availableSportsbooks, availableMarkets, availablePlayers,
//   }
export async function loadE2EQueryFixture(fileRelPath, queryIndex = 0) {
  const fileUrl = resolveFixtureUrl(fileRelPath);
  const data = await fetchJson(fileUrl);
  const results = Array.isArray(data?.results) ? data.results : [];
  const result = results[queryIndex] || results[0];
  if (!result) {
    throw new Error(`No results in fixture ${fileRelPath}`);
  }
  const rawItems = (result.items || []).map(normalizeItemDates);
  const transformed = transformStraightItems(rawItems, null, { skipStaleFilter: true });
  const fetchedOptions = hydrateResultsForRender(transformed);
  const inferredReferenceTime = inferReferenceTimeFromItems(rawItems);

  return {
    query: result.q || result.prompt || result.query || '',
    referenceTime: result.referenceTime || data?.ticker?.referenceTime || inferredReferenceTime || null,
    classification: result.classification || null,
    rawItems,
    fetchedOptions,
    ...extractResultFacets(fetchedOptions, result.q || result.prompt || result.query || ''),
  };
}

// List local fixture files. On web we can't readdir, so we fetch a manifest.
// For now we hardcode the known prefix and let the user paste a path; the
// lab UI also offers a file picker as the primary path.
export const KNOWN_FIXTURE_GLOBS = [
  'scripts/output/human-search-e2e-*.json',
  'scripts/output/production-search-e2e-*.json',
];

// Resolve a relative fixture path to a fetchable URL. Only files under
// website/public/ are served by Expo — scripts/output/ is NOT reachable via
// fetch (returns the SPA index.html → JSON parse error). Use Pick fixture file.
function resolveFixtureUrl(relPath) {
  if (!relPath) return null;
  if (/^https?:\/\//i.test(relPath)) return relPath;
  const clean = relPath.replace(/^\.?\//, '');
  if (clean.startsWith('scripts/') || clean.startsWith('(captured:')) return null;
  return `/${clean}`;
}

async function fetchJson(url) {
  if (!url) {
    throw new Error(
      'Load by path cannot read scripts/output files from the dev server. Use "Pick fixture file" instead (reads the file from disk via the browser file picker).'
    );
  }
  const res = await fetch(url, { cache: 'no-store' });
  const text = await res.text();
  if (!res.ok) throw new Error(`Fixture fetch failed: ${res.status} ${url}`);
  const trimmed = text.trim();
  if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
    throw new Error(
      'Load by path returned HTML, not JSON. The dev server does not expose scripts/output/. Use "Pick fixture file" to load E2E JSON from disk.'
    );
  }
  try {
    return JSON.parse(text);
  } catch (e) {
    throw new Error(`Fixture is not valid JSON: ${e.message}`);
  }
}

// Read a user-picked File object (from <input type=file>) and parse it.
// Used by the lab's file picker so we don't depend on the dev server
// having the scripts/output directory mounted.
export async function loadFixtureFromFile(file) {
  const text = await file.text();
  const data = JSON.parse(text);
  return data; // caller routes to loadE2EQueryFixture via a synthetic path
}

// Same as loadE2EQueryFixture but takes an already-parsed JSON object
// (from the file picker). queryIndex picks which query's items to load.
export function loadFixtureFromObject(data, queryIndex = 0) {
  const preResults = Array.isArray(data?.results) ? data.results : [];
  const prompt = formatQueryLabel(
    preResults[queryIndex]?.q || preResults[queryIndex]?.prompt || preResults[0]?.q || preResults[0]?.prompt,
    ''
  );
  const normalized = normalizeNlOddsPayload(data, prompt);
  const results = Array.isArray(normalized?.results) ? normalized.results : [];
  const result = results[queryIndex] || results[0];
  if (!result) throw new Error('No results in fixture object');
  const rawItems = (result.items || []).map(normalizeItemDates);
  const transformed = transformStraightItems(rawItems, null, { skipStaleFilter: true });
  const fetchedOptions = hydrateResultsForRender(transformed);
  const inferredReferenceTime = inferReferenceTimeFromItems(rawItems);
  return {
    query: formatQueryLabel(result.q || result.prompt || result.query, ''),
    referenceTime: result.referenceTime || normalized?.ticker?.referenceTime || inferredReferenceTime || null,
    classification: result.classification || normalized?._queryClassification || null,
    rawItems,
    transformedCount: transformed.length,
    fetchedOptions,
    ...extractResultFacets(fetchedOptions, formatQueryLabel(result.q || result.prompt || result.query, '')),
    availableQueries: results.map((r, i) => ({
      index: i,
      q: formatQueryLabel(r.q || r.prompt || r.query, `Query ${i + 1}`),
      count: r.items?.length || 0,
    })),
  };
}

